import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../../../api/client';
import { toast } from 'react-toastify';
import { theme } from '../../../../../theme';

const useJobs = (companyId = null) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState('candidates'); // 'candidates' or 'interviews'
  
  // Jobs data
  const [allCandidatesJobs, setAllCandidatesJobs] = useState([]);
  const [allInterviewsJobs, setAllInterviewsJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchingPage, setFetchingPage] = useState(null);
  
  // Refs for polling
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  const fetchAllPages = async (reportType, compId = null) => {
    let allResults = [];
    let page = 1;
    let totalPages = null;
    
    try {
      while (true) {
        setFetchingPage(`${reportType} page ${page}${totalPages ? ` of ${totalPages}` : ''}`);
        
        let url = `reports/jobs/?report_type=${reportType}&page=${page}`;
        if (compId) {
          url += `&company_id=${compId}`;
        }
        const response = await api.get(url);
        const data = response.data;
        
        if (totalPages === null && data.count) {
          const pageSize = data.results.length;
          totalPages = Math.ceil(data.count / pageSize);
        }
        
        allResults = [...allResults, ...data.results];        
        if (!data.next || allResults.length >= data.count) {
          break;
        }
        
        page++;
        
        if (page > 200) {
          break;
        }
      }
            return allResults;
      
    } catch (err) {
      throw new Error(`Failed to fetch ${reportType} jobs: ${err.message}`);
    } finally {
      setFetchingPage(null);
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setFetchingPage('Initializing...');
      const [candidatesData, interviewsData] = await Promise.all([
        fetchAllPages('candidates', companyId),
        fetchAllPages('interviews', companyId)
      ]);

      const sortedCandidates = candidatesData.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      const sortedInterviews = interviewsData.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setAllCandidatesJobs(sortedCandidates);
      setAllInterviewsJobs(sortedInterviews);

    } catch (err) {
      const errorMessage = err.message || 'Unknown error occurred while fetching jobs';
      setError(errorMessage);
      setAllCandidatesJobs([]);
      setAllInterviewsJobs([]);
    } finally {
      setLoading(false);
      setFetchingPage(null);
    }
  }, [companyId]);


  // Function to check status of specific jobs

  const checkJobsStatus = useCallback(async (jobIds) => {
    if (!jobIds.length) return;
    try {
      const statusPromises = jobIds.map(async (jobId) => {
        try {
          const response = await api.get(`reports/jobs/${jobId}/`);
          return response.data;
        } catch (err) {
          console.error(`Failed to check status for job ${jobId}:`, err);
          return null;
        }
      });

      const updatedJobs = (await Promise.all(statusPromises)).filter(Boolean);
      
      if (updatedJobs.length > 0) {
        // Find previously active jobs that are now finished
        updatedJobs.forEach(updatedJob => {
          const prevJob = [...allCandidatesJobs, ...allInterviewsJobs].find(j => j.id === updatedJob.id);
          if (prevJob && (prevJob.status === 'pending' || prevJob.status === 'processing')) {
            if (updatedJob.status === 'completed') {
              toast.success(`Report generation completed! You can now download it.`, { 
                style: { backgroundColor: theme.colors.success[500], color: theme.colors.white } 
              });
            } else if (updatedJob.status === 'failed') {
              toast.error(`Report generation failed: ${updatedJob.error_message || 'Unknown error'}`, { 
                style: { backgroundColor: theme.colors.error[500], color: theme.colors.white } 
              });
            }
          }
        });

        // Update candidates jobs
        setAllCandidatesJobs(prev => 
          prev.map(job => {
            const updated = updatedJobs.find(u => u.id === job.id);
            if (updated) {
              return { ...job, ...updated };
            }
            return job;
          })
        );

        // Update interviews jobs
        setAllInterviewsJobs(prev => 
          prev.map(job => {
            const updated = updatedJobs.find(u => u.id === job.id);
            if (updated) {
              return { ...job, ...updated };
            }
            return job;
          })
        );
      }

    } catch (err) {
      console.error('Error checking jobs status:', err);
    }
  }, [allCandidatesJobs, allInterviewsJobs]);

  // Start polling for pending/processing jobs (60 seconds interval)
  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;

    pollingIntervalRef.current = setInterval(() => {
      const allJobs = [...allCandidatesJobs, ...allInterviewsJobs];
      const activeJobs = allJobs.filter(job => 
        job.status === 'pending' || job.status === 'processing'
      );

      if (activeJobs.length > 0) {
        checkJobsStatus(activeJobs.map(job => job.id));
      } else {
        stopPolling();
      }
    }, 10000); // 10 seconds
  },
 // eslint-disable-next-line react-hooks/exhaustive-deps
  [allCandidatesJobs, allInterviewsJobs, checkJobsStatus]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  // Effect to manage polling based on job states
  useEffect(() => {
    const allJobs = [...allCandidatesJobs, ...allInterviewsJobs];
    const activeJobs = allJobs.filter(job => 
      job.status === 'pending' || job.status === 'processing'
    );

    if (activeJobs.length > 0 && !loading) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [allCandidatesJobs, allInterviewsJobs, loading, startPolling, stopPolling]);

  const refreshJobs = useCallback(async () => {
    await fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Handle temporary jobs from report generation
  useEffect(() => {
    if (window.tempNewJob) {
      const tempJob = window.tempNewJob;      
      if (tempJob.report_type === 'candidates') {
        setAllCandidatesJobs(prev => {
          const exists = prev.some(job => job.id === tempJob.id);
          return exists ? prev : [tempJob, ...prev];
        });
      } else if (tempJob.report_type === 'interviews') {
        setAllInterviewsJobs(prev => {
          const exists = prev.some(job => job.id === tempJob.id);
          return exists ? prev : [tempJob, ...prev];
        });
      }
      
      delete window.tempNewJob;
    }
  }, [allCandidatesJobs, allInterviewsJobs]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Pagination logic - Updated to include both completed and failed jobs
  const getCurrentJobs = useCallback(() => {
    const jobs = activeTab === 'candidates' ? allCandidatesJobs : allInterviewsJobs;
    
    // Include both completed and failed jobs in the pagination
    const completedAndFailedJobs = jobs.filter(job => 
      job.status === 'completed' || job.status === 'failed'
    );
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      jobs: completedAndFailedJobs.slice(startIndex, endIndex),
      totalCount: completedAndFailedJobs.length,
      totalPages: Math.ceil(completedAndFailedJobs.length / pageSize)
    };
  }, [allCandidatesJobs, allInterviewsJobs, activeTab, currentPage, pageSize]);

  // Page change handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when switching tabs
  }, []);

  // Derived state
  const allJobs = [...allCandidatesJobs, ...allInterviewsJobs];
  const pendingJobs = allJobs.filter(job => 
    job.status === 'pending' || job.status === 'processing'
  );
  const completedJobs = allJobs.filter(job => 
    job.status === 'completed'
  );
  const failedJobs = allJobs.filter(job => 
    job.status === 'failed'
  );

  const paginationData = getCurrentJobs();

  return {
    // Original data (all jobs)
    candidatesJobs: allCandidatesJobs,
    interviewsJobs: allInterviewsJobs,
    allJobs,
    pendingJobs,
    completedJobs,
    failedJobs,
    
    // Pagination data
    currentJobs: paginationData.jobs,
    currentPage,
    pageSize,
    totalPages: paginationData.totalPages,
    totalCount: paginationData.totalCount,
    currentCount: paginationData.jobs.length,
    activeTab,
    
    // Handlers
    handlePageChange,
    handlePageSizeChange,
    handleTabChange,
    
    // Status
    loading,
    error,
    fetchingPage,
    refreshJobs,
    isPolling: isPollingRef.current,
    
    // Utilities
    totalJobs: allJobs.length,
    isEmpty: !loading && allJobs.length === 0,
    hasError: !!error
  };
};

export default useJobs;